import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { InfoDropdown } from 'src/app/components/faq/faq.component';
import { Registration } from 'src/app/models/registration';
import { RegistrationService } from 'src/app/services/registration-service';
import { ImageService } from 'src/app/services/image-service';
import { viewerConfigById } from 'src/app/consts/default-image';
import { SearchReqResponse, SearchService } from 'src/app/services/search-service';
import GlobalFaqs from '../../global-faqs';

/* =============================================================================
   PlateX: Number plates under £200 (dev2 Register restyle).
   Same visual language as the Number Ones / Dateless / New Release registers
   (hero + stats, sticky toolbar of segmented filters, canonical result rows),
   kept deliberately simple: one live list, filtered client-side.
   Data: plato-search `process=sub-200` (prices in pence).
============================================================================= */

export type U200Format = 'all' | 'dateless' | 'suffix' | 'prefix' | 'current';
export type U200Band = 'all' | 'u150' | 'u175' | 'u200';
export type U200Sort = 'price-asc' | 'price-desc' | 'az' | 'za';

interface FormatInfo {
  key: Exclude<U200Format, 'all'>;
  label: string;
  example: string;
  rule: string;
}

// Order = what most budget buyers want first: dateless fits any vehicle.
const FORMATS: FormatInfo[] = [
  { key: 'dateless', label: 'Dateless', example: 'AIZ 1234', rule: 'No age identifier, so it fits any vehicle. Under £200 these are Northern Irish style, with an I or Z in the letters.' },
  { key: 'suffix', label: 'Suffix', example: 'ABC 123D', rule: 'The year letter sits at the end (1963 to 1983). Fits vehicles first registered after that year.' },
  { key: 'prefix', label: 'Prefix', example: 'A123 BCD', rule: 'The year letter sits at the start (1983 to 2001). Fits vehicles first registered after that year.' },
  { key: 'current', label: 'Current', example: 'AB12 CDE', rule: 'The two-digit age identifier (2001 on). It can make a car look older, never newer.' },
];

// Upper bounds in pence (exclusive), lower bound of each band in pence.
const BANDS: { key: U200Band; label: string; min: number; max: number }[] = [
  { key: 'all', label: 'Any', min: 0, max: Infinity },
  { key: 'u150', label: 'Under £150', min: 0, max: 15000 },
  { key: 'u175', label: '£150 to £175', min: 15000, max: 17500 },
  { key: 'u200', label: '£175 to £200', min: 17500, max: Infinity },
];

@Component({
  selector: 'pla-under-200-register',
  templateUrl: './under-200-register.component.html',
  styleUrls: ['./under-200-register.component.scss'],
})
export class Under200RegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  public readonly formats = FORMATS;
  public readonly bands = BANDS;
  // Hero: the plate viewer's Fiat 500 wearing a real plate from this list
  // (the cheapest dateless one on sale), as the Dateless register does. The
  // existing SUB 200 banner shows until the render resolves or if WebGL fails.
  private readonly HERO_VIEWER_CONFIG_ID = 434; // Fiat 500 Riva
  public heroImage = 'https://cdn.platex.co.uk/banners/SUB_200_FIAT_500_DT.png';
  public heroPlate: Registration | null = null;

  public loading = true;
  private all: Registration[] = [];

  // filter state
  public format: U200Format = 'all';
  public band: U200Band = 'all';
  public term = '';
  public sort: U200Sort = 'price-asc';

  // derived
  public formatCounts: Record<U200Format, number> = { all: 0, dateless: 0, suffix: 0, prefix: 0, current: 0 };
  public formatFrom: Partial<Record<U200Format, number>> = {};
  public formatTotals: Partial<Record<U200Format, number>> = {};
  public shownCount = 0;
  public stats = { total: 0, from: 0, dateless: 0, under150: 0 };

  // canonical result table feed
  public view: SearchReqResponse = new SearchReqResponse('sub-200', []);
  public viewChange = new EventEmitter<SearchReqResponse>();

  public faq: InfoDropdown[] = GlobalFaqs.GetFaqsByKey('Sub £200');

  constructor(
    private el: ElementRef<HTMLElement>,
    private searchService: SearchService,
    private registrationService: RegistrationService,
    private imageService: ImageService,
  ) {}

  ngOnInit(): void {
    this.searchService.getSub200((rows: Registration[]) => {
      this.all = this.registrationService.formatRegistrations(rows || []);
      this.loading = false;
      this.buildStats();
      this.apply();
      this.buildHeroImage();
    });
  }

  ngAfterViewInit(): void {
    [0, 150, 500, 1200].forEach((t) => setTimeout(() => this.measureShell(), t));
    window.addEventListener('resize', this.measureShellBound, { passive: true });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.measureShellBound);
  }

  /* =============== filters =============== */
  public setFormat(f: U200Format, jump = false): void {
    this.format = f;
    this.apply();
    if (jump) this.jumpTo('plates');
  }
  public setBand(b: U200Band): void {
    this.band = b;
    this.apply();
  }
  public setSort(s: U200Sort): void {
    this.sort = s;
    this.apply();
  }
  public setTerm(raw: string): void {
    this.term = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    this.apply();
  }
  public get filtered(): boolean {
    return this.format !== 'all' || this.band !== 'all' || this.term !== '';
  }
  public clearFilters(): void {
    this.format = 'all';
    this.band = 'all';
    this.term = '';
    this.apply();
  }

  private inBand(r: Registration): boolean {
    const b = BANDS.find((x) => x.key === this.band);
    return r.price >= b.min && r.price < b.max;
  }
  private matchesTerm(r: Registration): boolean {
    return !this.term || r.registration.replace(/\s+/g, '').toUpperCase().indexOf(this.term) > -1;
  }

  private apply(): void {
    // Format counts are faceted: they respect price + search, not the format
    // itself, so each button says what picking it would show.
    const base = this.all.filter((r) => this.inBand(r) && this.matchesTerm(r));
    const counts: Record<U200Format, number> = { all: base.length, dateless: 0, suffix: 0, prefix: 0, current: 0 };
    for (const r of base) if (counts[r.style as U200Format] !== undefined) counts[r.style as U200Format]++;
    this.formatCounts = counts;

    const rows = this.format === 'all' ? base : base.filter((r) => r.style === this.format);
    rows.sort(this.comparator());
    this.shownCount = rows.length;
    this.view = new SearchReqResponse('sub-200', rows);
    this.viewChange.emit(this.view);
  }

  private comparator(): (a: Registration, b: Registration) => number {
    switch (this.sort) {
      case 'price-desc': return (a, b) => b.price - a.price;
      case 'az': return (a, b) => a.registration.localeCompare(b.registration);
      case 'za': return (a, b) => b.registration.localeCompare(a.registration);
      default: return (a, b) => a.price - b.price;
    }
  }

  private buildStats(): void {
    const prices = this.all.map((r) => r.price);
    this.stats = {
      total: this.all.length,
      from: prices.length ? Math.min(...prices) : 0,
      dateless: this.all.filter((r) => r.style === 'dateless').length,
      under150: this.all.filter((r) => r.price < 15000).length,
    };
    for (const f of FORMATS) {
      const p = this.all.filter((r) => r.style === f.key).map((r) => r.price);
      this.formatTotals[f.key] = p.length;
      if (p.length) this.formatFrom[f.key] = Math.min(...p);
    }
  }

  private buildHeroImage(): void {
    const dateless = this.all.filter((r) => r.style === 'dateless').sort((a, b) => a.price - b.price);
    const plate = dateless[0];
    const config = viewerConfigById(this.HERO_VIEWER_CONFIG_ID);
    if (!plate || !config) return; // banner stays
    try {
      this.imageService.quickBuild(config, plate.registration, (key) => {
        if (key && key.image) {
          this.heroImage = key.image;
          this.heroPlate = plate;
        }
      });
    } catch {
      /* WebGL unavailable: banner stays */
    }
  }

  public plateLink(reg: string): string[] {
    return ['/plate', reg.replace(/\s+/g, '')];
  }

  /* =============== helpers =============== */
  public pounds(pence: number | undefined): string {
    return pence === undefined ? '' : '£' + Math.round(pence / 100).toLocaleString('en-GB');
  }
  public num(n: number): string {
    return n.toLocaleString('en-GB');
  }

  public jumpTo(id: string, ev?: Event): void {
    if (ev) ev.preventDefault();
    const target = this.el.nativeElement.querySelector('#' + id) as HTMLElement | null;
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Same sticky-offset handling as the Number Ones register: the dev2 shell
  // scrolls away (toolbar sticks at 0); a fixed legacy header pins on top.
  private measureShellBound = (): void => this.measureShell();
  private measureShell(): void {
    const shell = (document.querySelector('pla-nav-shell') ||
      document.querySelector('.site-header-container .site-header')) as HTMLElement | null;
    let h = 0;
    if (shell) {
      const pos = getComputedStyle(shell).position;
      h = pos === 'fixed' ? Math.round(shell.getBoundingClientRect().height) : 0;
    }
    this.el.nativeElement.style.setProperty('--shellH', `${h}px`);
    const tb = this.el.nativeElement.querySelector('.toolbar') as HTMLElement | null;
    if (tb && tb.offsetHeight > 0) {
      this.el.nativeElement.style.setProperty('--tbH', `${Math.round(tb.offsetHeight)}px`);
    }
  }
}
